/**
 * Firestore helper utilities for Hugo_ prefixed collections.
 * Uses modular Firebase SDK v9+ with tree-shaking support.
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Collection name helpers with Hugo_ prefix
// ---------------------------------------------------------------------------

/** Map of logical names → Hugo_-prefixed Firestore collection names */
export const collections = {
  toolAccess: 'Hugo_ToolAccess',
  activityLog: 'Hugo_ActivityLog',
  organizations: 'Hugo_Organizations',
  surveys: 'Hugo_Surveys',
  responses: 'Hugo_Responses',
  draftResponses: 'Hugo_DraftResponses',
  oeSnapshots: 'Hugo_OESnapshots',
  oeAdjustments: 'Hugo_OEAdjustments',
  refReports: 'Hugo_REFReports',
  proactiveActions: 'Hugo_ProactiveActions',
  invites: 'Hugo_Invites',
  documents: 'Hugo_Documents',
  documentText: 'Hugo_DocumentText',         // n8n ingestion text cache
  riskAlerts: 'Hugo_RiskAlerts',
  guidanceContent: 'Hugo_GuidanceContent',
  caseStudies: 'Hugo_CaseStudies',
  patternLibrary: 'Hugo_PatternLibrary',
  trls: 'Hugo_TRLs',                          // Phase 0: ITM Trap/Roadblock/Landmine entries
  healthMessages: 'Hugo_HealthMessages',      // Phase 0: AI-generated Health Status Messages
  userPreferences: 'Hugo_UserPreferences',
  feedback: 'Hugo_Feedback',                  // Beta-1: Tell-the-Build-Team notes (relayed by n8n, P7-10)
};

/**
 * Get the Hugo_-prefixed collection name for a logical key.
 * Falls back to adding the prefix if the key is not in the map.
 * @param {string} key - Logical collection key (e.g. 'surveys') or raw name
 * @returns {string} The prefixed collection name
 */
export function getCollectionName(key) {
  if (collections[key]) return collections[key];
  // If already prefixed, return as-is
  if (key.startsWith('Hugo_')) return key;
  return `Hugo_${key}`;
}

/**
 * Get a Firestore collection reference by logical key or name.
 * @param {string} name - Logical key (e.g. 'surveys') or full collection name
 * @returns {CollectionReference}
 */
export function getCollection(name) {
  return collection(db, getCollectionName(name));
}

/**
 * Get a Firestore document reference.
 * @param {string} collectionName - Logical key or full collection name
 * @param {string} docId - Document ID
 * @returns {DocumentReference}
 */
export function getDocRef(collectionName, docId) {
  return doc(db, getCollectionName(collectionName), docId);
}

// ---------------------------------------------------------------------------
// Generic CRUD helpers
// ---------------------------------------------------------------------------

/**
 * Fetch all documents from a collection, with optional query constraints.
 * @param {string} collectionName - Logical key or full collection name
 * @param {Array} constraints - Firestore query constraints (where, orderBy, limit…)
 * @returns {Promise<Array<{id: string, ...data}>>}
 */
export async function fetchAll(collectionName, constraints = []) {
  const ref = getCollection(collectionName);
  const q = constraints.length > 0 ? query(ref, ...constraints) : ref;
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Fetch a single document by ID.
 * @param {string} collectionName - Logical key or full collection name
 * @param {string} docId - Document ID
 * @returns {Promise<{id: string, ...data} | null>}
 */
export async function fetchById(collectionName, docId) {
  const ref = getDocRef(collectionName, docId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Create a new document (auto-generated ID).
 * Automatically adds a createdAt timestamp.
 * @param {string} collectionName - Logical key or full collection name
 * @param {Object} data - Document data
 * @returns {Promise<string>} The new document ID
 */
export async function createDoc(collectionName, data) {
  const ref = getCollection(collectionName);
  const docRef = await addDoc(ref, {
    ...data,
    createdAt: Timestamp.now(),
  });
  return docRef.id;
}

/**
 * Create or overwrite a document with a specific ID.
 * Automatically adds a createdAt timestamp if creating new.
 * @param {string} collectionName - Logical key or full collection name
 * @param {string} docId - Document ID
 * @param {Object} data - Document data
 * @param {Object} options - setDoc options (e.g. { merge: true })
 * @returns {Promise<void>}
 */
export async function createDocWithId(collectionName, docId, data, options = {}) {
  const ref = getDocRef(collectionName, docId);
  await setDoc(ref, {
    ...data,
    createdAt: Timestamp.now(),
  }, options);
}

/**
 * Update fields on an existing document.
 * Automatically adds an updatedAt timestamp.
 * @param {string} collectionName - Logical key or full collection name
 * @param {string} docId - Document ID
 * @param {Object} data - Fields to update
 * @returns {Promise<void>}
 */
export async function updateDocument(collectionName, docId, data) {
  const ref = getDocRef(collectionName, docId);
  await updateDoc(ref, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a document by ID.
 * @param {string} collectionName - Logical key or full collection name
 * @param {string} docId - Document ID
 * @returns {Promise<void>}
 */
export async function removeDoc(collectionName, docId) {
  const ref = getDocRef(collectionName, docId);
  await deleteDoc(ref);
}

/**
 * Subscribe to real-time updates on a collection.
 * @param {string} collectionName - Logical key or full collection name
 * @param {Function} callback - Called with array of {id, ...data} on each update
 * @param {Array} constraints - Firestore query constraints
 * @returns {Function} Unsubscribe function
 */
export function subscribeToCollection(collectionName, callback, constraints = []) {
  const ref = getCollection(collectionName);
  const q = constraints.length > 0 ? query(ref, ...constraints) : ref;
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(items);
  });
}

/**
 * Subscribe to real-time updates on a single document.
 * @param {string} collectionName - Logical key or full collection name
 * @param {string} docId - Document ID
 * @param {Function} callback - Called with {id, ...data} or null on each update
 * @returns {Function} Unsubscribe function
 */
export function subscribeToDoc(collectionName, docId, callback) {
  const ref = getDocRef(collectionName, docId);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) {
      callback(null);
    } else {
      callback({ id: snap.id, ...snap.data() });
    }
  });
}

// ---------------------------------------------------------------------------
// Timestamp conversion utilities
// ---------------------------------------------------------------------------

/**
 * Convert a Firestore Timestamp to a JavaScript Date.
 * Safely handles null/undefined and plain Date objects.
 * @param {Timestamp|Date|null} ts - Firestore Timestamp or Date
 * @returns {Date|null}
 */
export function timestampToDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (ts.toDate && typeof ts.toDate === 'function') return ts.toDate();
  // Handle seconds-based object ({ seconds, nanoseconds })
  if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
  return null;
}

/**
 * Convert a JavaScript Date (or date string) to a Firestore Timestamp.
 * @param {Date|string|number} date - Date, ISO string, or epoch ms
 * @returns {Timestamp}
 */
export function dateToTimestamp(date) {
  if (date instanceof Timestamp) return date;
  const d = date instanceof Date ? date : new Date(date);
  return Timestamp.fromDate(d);
}

/**
 * Format a Firestore Timestamp to a locale string.
 * @param {Timestamp|Date|null} ts - Firestore Timestamp or Date
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string or empty string
 */
export function formatTimestamp(ts, options = {}) {
  const date = timestampToDate(ts);
  if (!date) return '';
  const defaults = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return date.toLocaleString(undefined, { ...defaults, ...options });
}

/**
 * Get a Firestore server timestamp (for use in writes).
 * @returns {FieldValue}
 */
export function getServerTimestamp() {
  return serverTimestamp();
}

/**
 * Get the current time as a Firestore Timestamp.
 * @returns {Timestamp}
 */
export function now() {
  return Timestamp.now();
}

// ---------------------------------------------------------------------------
// Activity logging
// ---------------------------------------------------------------------------

/**
 * Log an activity to the Hugo_ActivityLog collection.
 * @param {string} userId - User UID
 * @param {string} action - Action description
 * @param {string} toolSlug - Tool identifier (e.g. 'drift-accumulation')
 * @param {Object} details - Additional metadata
 */
export async function logActivity(userId, action, toolSlug = '', details = {}) {
  try {
    await addDoc(collection(db, collections.activityLog), {
      userId,
      action,
      toolSlug,
      details,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Failed to log activity:', error);
  }
}

// ---------------------------------------------------------------------------
// Re-exports for convenience (modular SDK v9+)
// ---------------------------------------------------------------------------

export {
  db,
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  serverTimestamp,
};
